import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RouteAwareThemeProvider } from "@/components/theme/RouteAwareThemeProvider";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { RequireAuth } from "@/components/RequireAuth";
import { RouteFallback } from "@/components/RouteFallback";
import { AuthQueryCacheBoundary } from "@/context/AuthQueryCacheBoundary";
import { DawnHost } from "@/features/entry/dawn/DawnHost";
import { isUnauthorizedError, logoutAndRedirect } from "@/lib/http";

import { DEV_ROUTES } from "./devRoutes";
import { loadAdminLoginPage, loadLoginPage } from "./pages/loaders";

const handleAuthError = (error: unknown) => {
  if (isUnauthorizedError(error)) {
    logoutAndRedirect();
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      handleAuthError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      handleAuthError(error);
    },
  }),
});

const Index = lazy(() => import("./pages/Index"));
const Login = lazy(loadLoginPage);
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminLogin = lazy(loadAdminLoginPage);
const NotFound = lazy(() => import("./pages/NotFound"));
const FeedbackLanding = lazy(() => import("./pages/FeedbackLanding"));
const BigFiveSurvey = lazy(() => import("./pages/BigFiveSurvey"));
const SignDocumentPage = lazy(() => import("./pages/SignDocument"));
const ParticipantSignDocumentPage = lazy(
  () => import("./pages/ParticipantSignDocument"),
);
const SignSuccessPage = lazy(() => import("./pages/SignSuccess"));

const DatenschutzAgreementPage = lazy(() =>
  import("./features/datenschutz-agreement").then((module) => ({
    default: module.DatenschutzAgreementPage,
  })),
);
const DatenschutzAgreementSuccessPage = lazy(() =>
  import("./features/datenschutz-agreement").then((module) => ({
    default: module.DatenschutzAgreementSuccessPage,
  })),
);

const DozentLayout = lazy(() => import("./pages/dozent/DozentLayout"));
const DozentDashboard = lazy(() => import("./pages/dozent/Dashboard"));
const DozentKategorien = lazy(() => import("./pages/dozent/Kategorien"));
const DozentKategorieDetail = lazy(
  () => import("./pages/dozent/KategorieDetail"),
);
const DozentVideoPlayer = lazy(() => import("./pages/dozent/VideoPlayer"));
const DozentProfil = lazy(() => import("./pages/dozent/Profil"));
const DozentCustomers = lazy(() => import("./pages/dozent/MeineKunden"));
const DozentCustomerDetails = lazy(
  () => import("./pages/dozent/MeineKundenDetails"),
);
const DozentCourses = lazy(() => import("./pages/dozent/MeineKurse"));
const DozentCourseDetail = lazy(
  () => import("./pages/dozent/MeineKurseDetail"),
);
const DozentCourseParticipantDetail = lazy(
  () => import("./pages/dozent/MeineKurseParticipantDetail"),
);
const DozentRaumbelegungPage = lazy(
  () => import("@/features/raumbelegung/pages/DozentRaumbelegungPage"),
);

const HrLandingPage = lazy(() =>
  import("./features/hr/components/HrDashboard").then((m) => ({
    default: m.HrLandingPage,
  })),
);
const HrInboxPage = lazy(() =>
  import("./features/hr/components/HrApprovalInbox").then((m) => ({
    default: m.HrInboxPage,
  })),
);
const HrRequestPage = lazy(() =>
  import("./features/hr/components/HrRequestDetail").then((m) => ({
    default: m.HrRequestPage,
  })),
);
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminUebersicht = lazy(() => import("./pages/admin/Uebersicht"));
const AdminDozenten = lazy(() => import("./pages/admin/Dozenten"));
const AdminDozentDetail = lazy(() => import("./pages/admin/DozentDetail"));
const AdminDozentFortbildungen = lazy(
  () => import("./pages/admin/DozentFortbildungen"),
);
const AdminDozentInvite = lazy(() => import("./pages/admin/DozentInvite"));
const AdminOnboarding = lazy(() => import("./pages/admin/Onboarding"));
const AdminEinstellungen = lazy(() => import("./pages/admin/Einstellungen"));
const AdminBerichte = lazy(() => import("./pages/admin/Berichte"));
const AdminAuftraggeber = lazy(() => import("./pages/admin/Auftraggeber"));
const AdminAuftraggeberDetail = lazy(
  () => import("./pages/admin/AuftraggeberDetail"),
);
const ParticipantsPage = lazy(() => import("./pages/admin/Teilnehmer"));
const ParticipantDetailPage = lazy(
  () => import("./pages/admin/TeilnehmerDetail"),
);
const CoachingsPage = lazy(() => import("./pages/admin/Coachings"));
const AdminAufgaben = lazy(() => import("./pages/admin/Aufgaben"));
const GruppenmassnahmeDetailPage = lazy(
  () => import("./pages/admin/GruppenmassnahmeDetail"),
);
const GruppenmassnahmeParticipantDetailPage = lazy(
  () => import("./pages/admin/GruppenmassnahmeParticipantDetail"),
);
const CoachingDetailPage = lazy(() => import("./pages/admin/CoachingDetail"));
const AdminAutomatisierungen = lazy(
  () => import("./pages/admin/Automatisierungen"),
);
const AutomatisierungEditorPage = lazy(
  () => import("./pages/admin/AutomatisierungEditor"),
);
const AdminProfil = lazy(() => import("./pages/admin/Profil"));
const AdminVorlagenPage = lazy(() => import("./pages/admin/Vorlagen"));
const AdminVorlageEditor = lazy(() => import("./pages/admin/VorlageEditor"));
const AdminGespraechkonstruktor = lazy(
  () => import("./pages/admin/Gespraechkonstruktor"),
);
const AdminQmHandbuch = lazy(() => import("./pages/admin/QmHandbuch"));
const AdminQmHandbuchBuilder = lazy(
  () => import("./pages/admin/QmHandbuchBuilder"),
);
const AdminKnowledgeBasePage = lazy(
  () => import("./pages/admin/KnowledgeBase"),
);
const AdminKnowledgeBaseEditorPage = lazy(
  () => import("./pages/admin/KnowledgeBaseEditor"),
);
const AdminRaumbelegungPage = lazy(
  () => import("@/features/raumbelegung/pages/AdminRaumbelegungPage"),
);
const AccessPermissionPage = lazy(() =>
  import("@/features/access-permission").then((module) => ({
    default: module.AccessPermissionPage,
  })),
);

const ParticipantLoginPage = lazy(() => import("./pages/participant/Login"));
const ParticipantPanelPage = lazy(() => import("./pages/participant/Panel"));
const ParticipantPortalLoginPage = lazy(
  () => import("./pages/participant/portal/Login"),
);
const ParticipantPortalPage = lazy(
  () => import("./pages/participant/portal/Portal"),
);
const ParticipantPortalHome = lazy(() =>
  import("@/features/participant-portal/pages/ParticipantPortalHome").then(
    (module) => ({ default: module.ParticipantPortalHome }),
  ),
);
const ParticipantPortalLearning = lazy(() =>
  import("@/features/participant-portal/pages/ParticipantPortalLearning").then(
    (module) => ({ default: module.ParticipantPortalLearning }),
  ),
);
const ParticipantPortalMeetings = lazy(() =>
  import("@/features/participant-portal/pages/ParticipantPortalMeetings").then(
    (module) => ({ default: module.ParticipantPortalMeetings }),
  ),
);
const ParticipantPortalProfile = lazy(() =>
  import("@/features/participant-portal/pages/ParticipantPortalProfile").then(
    (module) => ({ default: module.ParticipantPortalProfile }),
  ),
);
const ParticipantPortalGruppenmassnahmeSignatures = lazy(() =>
  import(
    "@/features/participant-gruppenmassnahme-signatures/pages/ParticipantPortalGruppenmassnahmeSignatures"
  ).then((module) => ({
    default: module.ParticipantPortalGruppenmassnahmeSignatures,
  })),
);
// Empty outside development (see devRoutes.ts). React.lazy runs once per route
// at module scope, never during render.
const DEV_PAGES = DEV_ROUTES.map(({ path, load }) => ({
  path,
  Page: lazy(load),
}));

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthQueryCacheBoundary />
    <BrowserRouter>
      <DawnHost />
      <RouteAwareThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/passwort-vergessen" element={<ForgotPassword />} />
              <Route
                path="/passwort-zuruecksetzen"
                element={<ResetPassword />}
              />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route
                path="/datenschutz"
                element={<DatenschutzAgreementPage />}
              />
              <Route
                path="/datenschutz/success"
                element={<DatenschutzAgreementSuccessPage />}
              />
              <Route path="/feedback/:token" element={<FeedbackLanding />} />
              <Route path="/bigfive/:token" element={<BigFiveSurvey />} />
              <Route path="/sign/success" element={<SignSuccessPage />} />
              <Route path="/sign/:token" element={<SignDocumentPage />} />
              <Route
                path="/participant-sign/success"
                element={<SignSuccessPage />}
              />
              <Route
                path="/participant-sign/:token"
                element={<ParticipantSignDocumentPage />}
              />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/participant/login"
                element={<ParticipantLoginPage />}
              />
              <Route
                path="/participant/panel/:coachingId"
                element={<ParticipantPanelPage />}
              />
              <Route
                path="/participant/portal/login"
                element={<ParticipantPortalLoginPage />}
              />
              <Route
                path="/participant/portal"
                element={<ParticipantPortalPage />}
              >
                <Route index element={<Navigate to="home" replace />} />
                <Route path="home" element={<ParticipantPortalHome />} />
                <Route
                  path="unterschriften/gruppenmassnahmen"
                  element={<ParticipantPortalGruppenmassnahmeSignatures />}
                />
                <Route path="lernen" element={<ParticipantPortalLearning />} />
                <Route path="treffen" element={<ParticipantPortalMeetings />} />
                <Route path="profil" element={<ParticipantPortalProfile />} />
              </Route>

              {/* Dozent Routes */}
              <Route
                path="/dozent"
                element={
                  <RequireAuth roles={["DOZENT"]} redirectTo="/login">
                    <DozentLayout />
                  </RequireAuth>
                }
              >
                <Route path="dashboard" element={<DozentDashboard />} />
                <Route path="kategorien" element={<DozentKategorien />} />
                <Route
                  path="kategorien/:kategorieId"
                  element={<DozentKategorieDetail />}
                />
                <Route path="thema/:themaId" element={<DozentVideoPlayer />} />
                <Route path="kunden" element={<DozentCustomers />} />
                <Route
                  path="kunden/:coachingId"
                  element={<DozentCustomerDetails />}
                />
                <Route
                  path="raumbelegung"
                  element={<DozentRaumbelegungPage />}
                />
                <Route path="kurse" element={<DozentCourses />} />
                <Route
                  path="kurse/:gruppenmassnahmeId"
                  element={<DozentCourseDetail />}
                />
                <Route
                  path="kurse/:gruppenmassnahmeId/participants/:participantId"
                  element={<DozentCourseParticipantDetail />}
                />
                <Route
                  path="zertifikate"
                  element={
                    <Navigate replace to="/dozent/profil?tab=zertifikate" />
                  }
                />
                <Route path="profil" element={<DozentProfil />} />
              </Route>

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <RequireAuth roles={["ADMIN"]} redirectTo="/admin/login">
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route path="uebersicht" element={<AdminUebersicht />} />
                <Route path="hr" element={<HrLandingPage />} />
                <Route path="hr/inbox" element={<HrInboxPage />} />
                <Route path="hr/requests/:id" element={<HrRequestPage />} />
                <Route
                  path="uebersicht/edit"
                  element={
                    <Navigate replace to="/admin/profil?tab=uebersicht" />
                  }
                />
                <Route path="dozenten" element={<AdminDozenten />} />
                <Route
                  path="dozenten/einladen"
                  element={<AdminDozentInvite />}
                />
                <Route
                  path="dozenten/fortbildungen"
                  element={<AdminDozentFortbildungen />}
                />
                <Route
                  path="dozenten/:dozentId"
                  element={<AdminDozentDetail />}
                />
                <Route path="auftraggeber" element={<AdminAuftraggeber />} />
                <Route
                  path="auftraggeber/:id"
                  element={<AdminAuftraggeberDetail />}
                />
                <Route path="teilnehmer" element={<ParticipantsPage />} />
                <Route
                  path="teilnehmer/:id"
                  element={<ParticipantDetailPage />}
                />
                <Route path="coachings" element={<CoachingsPage />} />
                <Route
                  path="coachings/:coachingId"
                  element={<CoachingDetailPage />}
                />
                <Route
                  path="standorte/raeumlichkeiten"
                  element={<AdminRaumbelegungPage />}
                />
                <Route path="aufgaben" element={<AdminAufgaben />} />
                <Route
                  path="gruppenmassnahmen/:gruppenmassnahmeId"
                  element={<GruppenmassnahmeDetailPage />}
                />
                <Route
                  path="gruppenmassnahmen/:gruppenmassnahmeId/participants/:participantId"
                  element={<GruppenmassnahmeParticipantDetailPage />}
                />
                <Route
                  path="automatisierungen"
                  element={<AdminAutomatisierungen />}
                />
                <Route
                  path="automatisierungen/:automationId"
                  element={<AutomatisierungEditorPage />}
                />
                <Route path="profil" element={<AdminProfil />} />
                <Route path="onboarding" element={<AdminOnboarding />} />
                <Route
                  path="onboarding/inhalte"
                  element={<AdminOnboarding />}
                />
                <Route
                  path="onboarding/test-ergebnisse"
                  element={<AdminOnboarding />}
                />
                <Route
                  path="inhalte"
                  element={<Navigate to="/admin/onboarding/inhalte" replace />}
                />
                <Route path="berichte" element={<AdminBerichte />} />
                <Route path="einstellungen" element={<AdminEinstellungen />} />
                <Route
                  path="settings/access-permission"
                  element={<AccessPermissionPage />}
                />
                <Route path="vorlagen" element={<AdminVorlagenPage />} />
                <Route path="vorlagen/neu" element={<AdminVorlageEditor />} />
                <Route path="vorlagen/:id" element={<AdminVorlageEditor />} />
                <Route
                  path="gespraechkonstruktor"
                  element={<AdminGespraechkonstruktor />}
                />
                <Route path="qm-handbuch" element={<AdminQmHandbuch />} />
                <Route
                  path="qm-handbuch/builder"
                  element={<AdminQmHandbuchBuilder />}
                />
                <Route
                  path="knowledge-base"
                  element={<AdminKnowledgeBasePage />}
                />
                <Route
                  path="knowledge-base/new"
                  element={<AdminKnowledgeBaseEditorPage />}
                />
                <Route
                  path="knowledge-base/:articleId"
                  element={<AdminKnowledgeBaseEditorPage />}
                />
              </Route>

              {DEV_PAGES.map(({ path, Page }) => (
                <Route key={path} path={path} element={<Page />} />
              ))}

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </TooltipProvider>
      </RouteAwareThemeProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
